import * as React from 'react';

import {
  Col,
  ContentCard,
  ContentSection,
  PhotoPreviewDialog,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import { useTicketFiles } from '../hooks/useTicketFiles';
import { ScaleTicketPhotoGrid } from '../shared/ScaleTicketPhotoGrid';

import {
  $applyOptimisticUpdate,
  $markOptimisticUpdateError,
  $optimisticPhotoUpdates,
  $removeOptimisticUpdate,
} from '~/modules/trade/_state/files.state';
import {
  $ticketPhotos,
  $ticketPhotosWithOptimistic,
} from '~/modules/trade/_state/scaleTicketPhotos.state';
import { usePhotoPreview } from '~/modules/trade/hooks/usePhotoPreview';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { GenericFile } from '~/modules/trade/types/file';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { FilesTab } from '~/shared/components/files';
import { useFileDownload } from '~/shared/hooks/useFileDownload';

type InboundTicketTabPhotosProps = {
  ticket: PurchaseLoadRecord | null;
};

export const InboundTicketTabPhotos: React.FC<InboundTicketTabPhotosProps> = ({
  ticket,
}) => {
  const t = useTranslations();

  const deleteFileRef = React.useRef<
    ((params: { fileId: string }) => Promise<void>) | null
  >(null);

  const ticketPhotosResult = useAtomValue($ticketPhotosWithOptimistic);
  const baseTicketPhotosResult = useAtomValue($ticketPhotos);
  const optimisticUpdates = useAtomValue($optimisticPhotoUpdates);
  const applyOptimisticUpdate = useSetAtom($applyOptimisticUpdate);
  const markOptimisticUpdateError = useSetAtom($markOptimisticUpdateError);
  const removeOptimisticUpdate = useSetAtom($removeOptimisticUpdate);

  const toast = useToast();
  const { downloadFile, isGeneratingDownloadUrl } = useFileDownload();
  const ticketFileMutations = useTicketFiles();

  const photoFiles = React.useMemo<GenericFile[]>(() => {
    if (ticketPhotosResult?.data) {
      return ticketPhotosResult.data
        .filter((photo) => photo.mimeType.startsWith('image/'))
        .map((photo) => ({
          id: photo.id,
          fileName: photo.fileName || `temp-${uuidv4()}`,
          fileSize: photo.fileSize,
          mimeType: photo.mimeType,
          status: photo.status,
          downloadUrl: photo.url,
          entityType: 'PurchaseLoad',
          entityId: ticket?.id,
          metadata: photo.metadata || {},
          createdAt: photo.createdAt?.toString(),
          updatedAt: photo.updatedAt?.toString(),
          _optimistic: photo._optimistic,
        }));
    }
    return [];
  }, [ticketPhotosResult?.data, ticket?.id]);

  const {
    previewOpen,
    currentPhotoIndex,
    previewPhotos,
    handlePhotoView,
    closePreview,
    navigatePreview,
  } = usePhotoPreview(photoFiles);

  const optimisticUpdatesFingerprint = React.useMemo(() => {
    const entries: string[] = [];
    optimisticUpdates.forEach((update, id) => {
      entries.push(`${id}:${update.type}:${update.status}`);
    });
    return entries.sort().join('|');
  }, [optimisticUpdates]);

  const renderDropZoneText = React.useMemo(
    () => ({
      primary: (
        <>
          {t.scaleTicketPro.photos.dropZone.primaryClick}{' '}
          <span className="font-semibold text-primary">
            {t.scaleTicketPro.photos.dropZone.primaryHere}
          </span>{' '}
          {t.scaleTicketPro.photos.dropZone.primaryDrag}{' '}
        </>
      ),
      secondary: t.scaleTicketPro.photos.dropZone.secondary,
    }),
    [t.scaleTicketPro.photos.dropZone],
  );

  const handleAllUploadsComplete = React.useCallback(() => {
    baseTicketPhotosResult.refetch();
  }, [baseTicketPhotosResult]);

  const handleDeletePhoto = React.useCallback(
    async (photoId: string) => {
      const currentPhoto = photoFiles.find((p) => p.id === photoId);
      if (!currentPhoto) return;

      applyOptimisticUpdate({
        id: photoId,
        type: 'delete',
        previousData: currentPhoto as Record<string, unknown>,
      });

      try {
        await deleteFileRef.current?.({ fileId: photoId });
        toast.success(t.scaleTicketPro.photos.photoDeleted);
      } catch (error) {
        console.error('Failed to delete photo:', error);
        markOptimisticUpdateError({
          id: photoId,
          error: t.scaleTicketPro.photos.photoDeleteFailed,
        });
        toast.error(t.scaleTicketPro.photos.photoDeleteFailed);
      }
    },
    [photoFiles, applyOptimisticUpdate, markOptimisticUpdateError, t, toast],
  );

  const handleRetryDelete = React.useCallback(
    async (photoId: string) => {
      const optimisticUpdate = optimisticUpdates.get(photoId);
      if (!optimisticUpdate || optimisticUpdate.status !== 'error') return;

      applyOptimisticUpdate({
        id: photoId,
        type: 'delete',
        previousData: optimisticUpdate.previousData,
      });

      try {
        await deleteFileRef.current?.({ fileId: photoId });
        toast.success(t.scaleTicketPro.photos.photoDeleted);
      } catch (_error) {
        markOptimisticUpdateError({
          id: photoId,
          error: t.scaleTicketPro.photos.photoDeleteFailed,
        });
        toast.error(t.scaleTicketPro.photos.photoDeleteFailed);
      }
    },
    [
      optimisticUpdates,
      applyOptimisticUpdate,
      markOptimisticUpdateError,
      toast,
      t,
    ],
  );

  const handleDismissOptimisticError = React.useCallback(
    (photoId: string) => {
      removeOptimisticUpdate(photoId);
    },
    [removeOptimisticUpdate],
  );

  React.useEffect(() => {
    deleteFileRef.current = ticketFileMutations.deleteFile;
  }, [ticketFileMutations.deleteFile]);

  React.useEffect(() => {
    if (!baseTicketPhotosResult.data) return;

    const serverPhotoIds = new Set(
      baseTicketPhotosResult.data.map((p) => p.id),
    );

    optimisticUpdates.forEach((update, photoId) => {
      if (update.type === 'delete' && !serverPhotoIds.has(photoId)) {
        removeOptimisticUpdate(photoId);
      }
    });
  }, [
    baseTicketPhotosResult.data,
    optimisticUpdatesFingerprint,
    removeOptimisticUpdate,
  ]);

  return (
    <Col gap5 className="@InboundTicketTabPhotos relative">
      <ContentCard className="overflow-visible pt-0">
        <ContentCard className="border-none bg-none px-0">
          <ContentSection
            title={t.scaleTicketPro.photos.title}
            titleClassName="font-bold text-neutral"
          >
            <FilesTab
              entityId={ticket?.id}
              files={photoFiles}
              mutations={ticketFileMutations}
              onAllUploadsComplete={handleAllUploadsComplete}
              mimeType="image/*"
              renderItems={(files, _defaultRenderer, uploadErrorActions) => (
                <ScaleTicketPhotoGrid
                  files={files}
                  uploadErrorActions={uploadErrorActions}
                  downloadFile={downloadFile}
                  isGeneratingDownloadUrl={isGeneratingDownloadUrl}
                  onDeletePhoto={handleDeletePhoto}
                  onRetryDelete={handleRetryDelete}
                  onDismissOptimisticError={handleDismissOptimisticError}
                  onView={handlePhotoView}
                />
              )}
              dropZoneTheme="compact"
              dropZoneText={renderDropZoneText}
            />
          </ContentSection>
        </ContentCard>
      </ContentCard>
      <PhotoPreviewDialog
        albumName={t.scaleTicketPro.photos.title}
        photos={previewPhotos}
        currentIndex={currentPhotoIndex}
        isOpen={previewOpen}
        onClose={closePreview}
        onNavigate={navigatePreview}
      />
    </Col>
  );
};
